import type { ReaderBlock } from '@bookclub/shared';

export type ReaderBlockContentItem = {
  kind: 'block';
  block: ReaderBlock;
};

export type ReaderRoundVideoSequenceContentItem = {
  kind: 'round_video_sequence';
  id: string;
  blocks: ReaderBlock[];
};

export type ReaderImageGalleryContentItem = {
  kind: 'image_gallery';
  id: string;
  blocks: ReaderBlock[];
};

export type ReaderGroupedContentItem =
  | ReaderRoundVideoSequenceContentItem
  | ReaderImageGalleryContentItem;

export type ReaderContentItem = ReaderBlockContentItem | ReaderGroupedContentItem;

type ReaderContentGroupKind = ReaderGroupedContentItem['kind'];

function getCompatibleGroupKind(block: ReaderBlock): ReaderContentGroupKind | null {
  if (block.type === 'video' && block.payload['style'] === 'round') {
    return 'round_video_sequence';
  }

  if (block.type === 'image') {
    return 'image_gallery';
  }

  return null;
}

function buildGroupId(kind: ReaderContentGroupKind, blocks: ReaderBlock[]) {
  return `${kind}:${blocks[0].id}:${blocks[blocks.length - 1].id}`;
}

function hasNonEmptyMediaCaption(block: ReaderBlock) {
  const caption = block.payload['caption'];
  return typeof caption === 'string' && caption.trim().length > 0;
}

function emitContentRun(kind: ReaderContentGroupKind | null, blocks: ReaderBlock[]): ReaderContentItem[] {
  if (!kind || (kind !== 'image_gallery' && blocks.length < 2)) {
    return blocks.map((block) => ({ kind: 'block', block }));
  }

  return [
    {
      kind,
      id: buildGroupId(kind, blocks),
      blocks,
    },
  ];
}

export function deriveReaderContentItems(blocks: ReaderBlock[]): ReaderContentItem[] {
  const items: ReaderContentItem[] = [];
  let activeKind: ReaderContentGroupKind | null = null;
  let activeRun: ReaderBlock[] = [];

  for (const block of blocks) {
    const kind = getCompatibleGroupKind(block);
    const startsNewCaptionedImageGallery =
      kind === 'image_gallery'
      && activeKind === 'image_gallery'
      && activeRun.length > 0
      && hasNonEmptyMediaCaption(block);

    if (activeRun.length > 0 && (kind !== activeKind || startsNewCaptionedImageGallery)) {
      items.push(...emitContentRun(activeKind, activeRun));
      activeRun = [];
    }

    activeKind = kind;
    activeRun.push(block);
  }

  if (activeRun.length > 0) {
    items.push(...emitContentRun(activeKind, activeRun));
  }

  return items;
}
